/* Inline formatter for content strings:
   **bold**, {{pc:C}} pitch-class name, {{n:C4}} note name, {{c:Am}} chord button that plays on click.
   Note names follow the user's naming setting. Text shown inside a button (a card, a play button, a quiz
   option) is formatted `inert`: chords become plain names there, since a button inside a button is invalid
   and a click on it would also run the outer button. */
import type { ReactNode } from 'react';
import { audio } from '../core/audio';
import { noteName, parseNote, parsePc, pcName, pretty } from '../core/theory';

const TOK = /\*\*(.+?)\*\*|\{\{(pc|n|c):([^}]+)\}\}/g;

const flatHint = (s: string, re: RegExp): boolean | undefined => (re.test(s) ? true : /#/.test(s) ? false : undefined);

function pcText(s: string): string {
  const pc = parsePc(s);
  return pc == null ? s : pcName(pc, { flat: flatHint(s, /b/) });
}

function noteText(s: string): string {
  const m = parseNote(s);
  return m == null ? s : noteName(m, { flat: flatHint(s, /^[A-G]b/) });
}

function format(str: string, keyPrefix: string, inert: boolean): ReactNode[] {
  const out: ReactNode[] = [];
  let last = 0;
  let k = 0;
  for (const m of str.matchAll(TOK)) {
    const idx = m.index ?? 0;
    if (idx > last) out.push(str.slice(last, idx));
    const key = `${keyPrefix}${k++}`;
    if (m[1] != null) out.push(<strong key={key}>{format(m[1], `${key}.`, inert)}</strong>);
    else if (m[2] === 'pc')
      out.push(
        <span key={key} className="tok-note">
          {pcText(m[3] as string)}
        </span>,
      );
    else if (m[2] === 'n')
      out.push(
        <span key={key} className="tok-note">
          {noteText(m[3] as string)}
        </span>,
      );
    else if (inert)
      out.push(
        <span key={key} className="tok-chord is-inert">
          {pretty(m[3] as string)}
        </span>,
      );
    else {
      const sym = m[3] as string;
      out.push(
        <button
          key={key}
          type="button"
          className="tok-chord"
          title={`Dinle: ${pretty(sym)}`}
          onClick={async (e) => {
            e.preventDefault();
            audio.stopAll();
            await audio.resume();
            audio.playChord(sym);
          }}
        >
          {pretty(sym)}
        </button>,
      );
    }
    last = idx + m[0].length;
  }
  if (last < str.length) out.push(str.slice(last));
  return out;
}

/** Renders a content string with inline markup; `inert` inside a button (no nested chord buttons). */
export function Rich({ text, inert = false }: { text: string | null | undefined; inert?: boolean }) {
  return <>{format(String(text ?? ''), 'r', inert)}</>;
}

/** Plain-text version of a content string (for aria labels, titles and review lists). */
export function richText(text: string | null | undefined): string {
  return String(text ?? '').replace(TOK, (_all, bold: string | undefined, kind: string, val: string) => {
    if (bold != null) return richText(bold);
    if (kind === 'pc') return pcText(val);
    if (kind === 'n') return noteText(val);
    return pretty(val);
  });
}
